import type {
    Tenant,
    CreateTenantRequest,
    UpdateTenantRequest,
    ApiResponse,
} from '@hyvay/shared-types';
import { mockTenants } from '../data/mockTenants';
import apiClient from './api';

// Using mock data for now - will be replaced with actual API calls
const USE_MOCK = false;

class TenantService {
    private tenants: Tenant[] = [...mockTenants];

    async getAllTenants(): Promise<ApiResponse<Tenant[]>> {
        if (USE_MOCK) {
            // ... mock logic ...
            return { success: true, data: this.tenants };
        }

        try {
            const response = await apiClient.get<ApiResponse<Tenant[]>>('/studio/applications');
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'FETCH_FAILED',
                message: error.message || 'Gagal mengambil data aplikasi',
            };
        }
    }

    async getTenantById(id: string): Promise<ApiResponse<Tenant>> {
        if (USE_MOCK) {
            // ... mock logic ...
            return { success: true, data: {} as Tenant };
        }

        try {
            const response = await apiClient.get<ApiResponse<Tenant>>(`/studio/applications/${id}`);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'FETCH_FAILED',
                message: error.message || 'Gagal mengambil data aplikasi',
            };
        }
    }

    async createTenant(data: CreateTenantRequest): Promise<ApiResponse<Tenant>> {
        if (USE_MOCK) {
            // ... mock logic ...
            return { success: true, data: {} as Tenant };
        }

        try {
            const response = await apiClient.post<ApiResponse<Tenant>>('/studio/applications', data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'CREATE_FAILED',
                message: error.message || 'Gagal membuat aplikasi baru',
            };
        }
    }

    async updateTenant(
        id: string,
        data: UpdateTenantRequest
    ): Promise<ApiResponse<Tenant>> {
        if (USE_MOCK) {
            await new Promise((resolve) => setTimeout(resolve, 400));

            const index = this.tenants.findIndex((t) => t.id === id);

            if (index === -1) {
                return {
                    success: false,
                    error: 'NOT_FOUND',
                    message: 'Aplikasi tidak ditemukan',
                };
            }

            this.tenants[index] = {
                ...this.tenants[index],
                ...data,
                updatedAt: new Date().toISOString(),
            };

            return {
                success: true,
                data: this.tenants[index],
            };
        }

        try {
            // Server route is PUT /studio/applications/:id (there is no /tenants API).
            const response = await apiClient.put<Tenant>(`/studio/applications/${id}`, data);
            return {
                success: true,
                data: response.data,
            };
        } catch (error: any) {
            return {
                success: false,
                error: 'UPDATE_FAILED',
                message: error.message || 'Gagal memperbarui aplikasi',
            };
        }
    }

    async deleteTenant(id: string): Promise<ApiResponse<void>> {
        if (USE_MOCK) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const index = this.tenants.findIndex((t) => t.id === id);

            if (index === -1) {
                return {
                    success: false,
                    error: 'NOT_FOUND',
                    message: 'Aplikasi tidak ditemukan',
                };
            }

            this.tenants.splice(index, 1);

            return {
                success: true,
                data: undefined,
            };
        }

        try {
            // Server route is /studio/applications/:id (there is no /tenants API).
            await apiClient.delete(`/studio/applications/${id}`);
            return {
                success: true,
                data: undefined,
            };
        } catch (error: any) {
            return {
                success: false,
                error: 'DELETE_FAILED',
                message: error.message || 'Gagal menghapus aplikasi',
            };
        }
    }
}

export const tenantService = new TenantService();
